import * as z from 'zod';

export const eventFormSchema = z
  .object({
    title: z.string().min(1, 'Title is required'),
    communityId: z.string().min(1, 'Community ID is required'),
    startDate: z.string().optional(),
    endDate: z.string().optional(),
    locationType: z.enum(['PHYSICAL', 'VIRTUAL', 'TO_BE_DECIDED']),
    address: z.string().optional(),
    virtualUrl: z.string().url('Invalid URL').optional().or(z.literal('')),
    googleMapLink: z.string().url('Invalid URL').optional().or(z.literal('')),
    privacy: z.enum(['PRIVATE', 'SEMI_PRIVATE', 'PUBLIC']),
    allowDuplicatePhone: z.boolean().default(true).optional(),
    isRegistrationClosed: z.boolean().default(false).optional(),
    markAttendance: z.boolean().default(false).optional(),
    eventType: z.enum(['BUSINESS', 'SOCIAL', 'CONCLAVE']).optional(),
    recurrenceType: z.enum(['ONE_TIME', 'RECURRING']).default('ONE_TIME').optional(),
    recurringFrequency: z.string().optional(),
    recurringStartDate: z.string().optional(),
    recurringEndDate: z.string().optional(),
    recurringTitle: z.string().optional(),
    recurringNotes: z.string().optional(),
    recurringDates: z.array(z.any()).optional(),
    banner: z.string().optional(),
    checkoutBgImage: z.string().nullable().optional(),
    slug: z
      .string()
      .regex(/^[a-z0-9-]*$/, 'Only lowercase letters, numbers, and hyphens allowed')
      .optional(),
    description: z.string().optional(),
    tickets: z
      .array(
        z.object({
          id: z.string().optional(),
          name: z.string().min(1, 'Name is required'),
          type: z.enum(['MEMBER_ONLY', 'MEMBER_GUEST', 'OPEN_FOR_ALL']),
          price: z.number().min(0),
          quantity: z.number().int().min(0).nullable().optional(),
          enabled: z.boolean().default(true),
          preset: z.enum(['member', 'guest']).optional(),
          allowedAttendeeTypes: z
            .array(z.string())
            .min(1, 'At least one attendee type is required'),
          formSchema: z
            .array(
              z.object({
                id: z.string(),
                type: z.enum([
                  'TEXT',
                  'TEXTAREA',
                  'NUMBER',
                  'SELECT',
                  'MULTI_SELECT',
                  'IMAGE',
                  'DATE',
                ]),
                label: z.string().min(1, 'Question label is required'),
                required: z.boolean(),
                options: z.array(z.string()).optional(),
                cropAspect: z.number().optional(),
                enableCrop: z.boolean().optional(),
              })
            )
            .optional(),
        })
      )
      .min(1, 'At least one ticket is required'),
  })
  .superRefine((data, ctx) => {
    if (data.recurrenceType !== 'RECURRING') {
      if (!data.startDate || !data.startDate.trim()) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Start date and time is required',
          path: ['startDate'],
        });
      }
      if (!data.endDate || !data.endDate.trim()) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'End date and time is required',
          path: ['endDate'],
        });
      }
    }

    const seenNames = new Map<string, number>();
    data.tickets.forEach((ticket, index) => {
      const normalized = ticket.name.trim().toLowerCase();
      if (!normalized) return;
      const firstIndex = seenNames.get(normalized);
      if (firstIndex != null) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Ticket names must be unique',
          path: ['tickets', index, 'name'],
        });
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Ticket names must be unique',
          path: ['tickets', firstIndex, 'name'],
        });
        return;
      }
      seenNames.set(normalized, index);
    });
  });

export const eventCreateFormSchema = eventFormSchema.superRefine((data, ctx) => {
  if (data.locationType === 'PHYSICAL' && !data.address?.trim()) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'Address is required',
      path: ['address'],
    });
  }
});

export type EventFormValues = z.infer<typeof eventFormSchema>;
